import Link from "next/link";
import { create } from "zustand";
import { Box } from "@mui/material";
import {
  getApplicationsMenu,
  getApplications,
  getInstructorApplication,
  getInstructorLists,
  getAllInstructorApplications,
} from "@/services/applications";
import { baseURL } from "@/services/http";
import _ from "lodash";

export const useApplicationsStore = create((set) => ({
  applications: [],
  applicationsMenu: [],
  instructorLists: null,
  instructorApplication: null,
  loading: false,
  loadingApps: false,
  loadingInstructorApp: false,
  error: "",
  successMessage: "",
  managedApplications: [],
  managedInstructorApplication: [],
  fetchApplicationsMenu: async (forceDisable) => {
    const { loading } = useApplicationsStore.getState();
    if (loading) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let appsInfo = await getApplicationsMenu();
      const applicationsMenu = buildMenu(appsInfo, forceDisable);
      set((state) => ({ ...state, error: "", applicationsMenu }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchApplications: async (userId, clearCache) => {
    const { applications, loadingApps } = useApplicationsStore.getState();
    if ((applications.length > 0 && !clearCache) || loadingApps) {
      return applications;
    }
    set((state) => ({ ...state, loadingApps: true }));
    try {
      const newOrgApplications = await getApplications(userId);
      // Only update the content if it's actually changed. This causes a refresh in the table unecessarily.
      if (!_.isEqual(newOrgApplications, applications)) {
        set((state) => ({
          ...state,
          error: "",
          applications: newOrgApplications,
        }));
      }
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Error fetching applications. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingApps: false,
      }));
    }
  },
  fetchInstructorApplication: async (userId) => {
    const {
      loadingApps,
      fetchApplications,
      instructorApplication,
      loadingInstructorApp,
    } = useApplicationsStore.getState();
    if (loadingApps || loadingInstructorApp) return null;
    if (instructorApplication) return instructorApplication;

    try {
      await fetchApplications(userId);
      const instructorApp = await getAllInstructorApplications(userId);
      if (instructorApp.length === 0) return null;
      set((state) => ({ ...state, loadingInstructorApp: true }));
      const { application_id } = instructorApp[0];
      let fullInstructorApp = await getInstructorApplication(application_id);
      // Short-circuit if this user doesn't have an instructor app
      if (!fullInstructorApp) return null;
      const instructorLists = await getInstructorLists();
      set((state) => ({
        ...state,
        error: "",
        instructorApplication: mapInstructorApp(
          fullInstructorApp,
          instructorLists
        ),
        instructorLists,
      }));
    } catch (ex) {
      set((state) => ({
        ...state,
        error:
          "Error fetching instructor application. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({ ...state, loadingInstructorApp: false }));
    }
  },
  setError: (error) => {
    set((state) => ({ ...state, error }));
  },
  setSuccessMessage: (message) => {
    set((state) => ({ ...state, successMessage: message }));
  },
  fetchManagedApplications: async (userId, clearCache) => {
    const { applications, loadingApps } = useApplicationsStore.getState();
    if ((applications.length > 0 && !clearCache) || loadingApps) {
      return applications;
    }
    set((state) => ({ ...state, loadingApps: true }));
    try {
      const managedApplications = await getApplications(userId);
      set((state) => ({ ...state, error: "", managedApplications }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "Error fetching applications. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingApps: false,
      }));
    }
  },
  fetchManagedInstructorApplication: async (managedUserId) => {
    const {
      loadingApps,
      fetchManagedApplications,
      managedInstructorApplication,
      loadingInstructorApp,
    } = useApplicationsStore.getState();
    if (loadingApps || loadingInstructorApp) return null;
    // if (managedInstructorApplication.length) return managedInstructorApplication;

    try {
      await fetchManagedApplications(managedUserId);
      const managedInstructorApp = await getAllInstructorApplications(
        managedUserId
      );
      const instructorLists = await getInstructorLists();
      if (managedInstructorApp.length) {
        set((state) => ({ ...state, loadingInstructorApp: true }));
        const { application_id } = managedInstructorApp[0];
        let fullInstructorApp = await getInstructorApplication(application_id);
        if (fullInstructorApp) {
          set((state) => ({
            ...state,
            error: "",
            managedInstructorApplication: mapInstructorApp(
              fullInstructorApp,
              instructorLists
            ),
            instructorLists: instructorLists,
            loadingInstructorApp: false,
          }));
          return;
        }
      }
      set((state) => ({
        ...state,
        error: "",
        managedInstructorApplication: [],
        instructorLists: instructorLists,
        loadingInstructorApp: false,
      }));
    } catch (ex) {
      set((state) => ({
        ...state,
        error:
          "Error fetching instructor application. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({ ...state, loadingInstructorApp: false }));
    }
  },
}));

const buildMenu = (applicationsUserInfo, forceDisable) => {
  const menu = [];
  menu.push({
    text: "Career Compass",
    expandOnClick: true,
    subItems: applicationsUserInfo.compass_apps.map((app) => ({
      text: app.name,
      navPath: `${baseURL}apps/compass/create/${app.id}`,
      disabled: !app.available || forceDisable,
    })),
    disabled: forceDisable,
  });
  const ecmhAction = applicationsUserInfo?.ecmh_apps?.canRenew
    ? "renew"
    : "edit";
  const isHidden = applicationsUserInfo.ecmh_apps.isHidden;
  if (!isHidden) {
    const ecmhMenuItem = {
      text: applicationsUserInfo.ecmh_apps.name,
      expandOnClick: true,
      additionalInfo:
        "In order to apply for the ECMH Credential a bachelor’s degree or higher in a related field from a CHEA-accredited college, a current professional license, or an ECMH Certificate is required",
      subItems: [
        {
          text: `${applicationsUserInfo.ecmh_apps.name} Application`,
          navPath: `applications/ecmh/${applicationsUserInfo.ecmh_apps.id}?action=${ecmhAction}&appid=0`,
          disabled:
            (!applicationsUserInfo.ecmh_apps.available &&
              !applicationsUserInfo?.ecmh_apps?.canRenew) ||
            forceDisable,
          disabledText: applicationsUserInfo.ecmh_apps.message,
        },
      ],
      disabled: forceDisable,
    };
    menu.push(ecmhMenuItem);
  }

  const eidsApps = applicationsUserInfo?.eids_apps;
  if (eidsApps && Object.keys(eidsApps).length > 0) {
    const eidsMenu = {
      text: "Early Intervention Developmental Specialist",
      expandOnClick: true,
      additionalInfo:
        "In order to apply for the EIDS an Associates degree or higher is required",
      subItems: Object.keys(eidsApps)
        .map((key) => {
          const initialInfo = eidsApps[key]["initial"];
          const renewalInfo = eidsApps[key]?.renewal;
          const available = initialInfo.can_initial || renewalInfo?.can_renewal;
          const action = renewalInfo?.can_renewal ? "renew" : "edit";

          const errorMessage =
            action === "renew" ? renewalInfo?.errors : initialInfo?.errors;

          return {
            text: eidsApps[key]["name"],
            navPath: `applications/eids/${key}?action=${action}&appid=0`,
            disabled: !available || forceDisable,
            disabledText: !available ? errorMessage[0] : "",
          };
        })
        .reverse(),
      disabled: forceDisable,
    };
    menu.push(eidsMenu);
  }

  const menteeApp = applicationsUserInfo?.mentee_app;
  if (menteeApp?.is_project_active) {
    let validationMsg = "";
    const awardId = menteeApp?.award_id;
    menteeApp?.validation_messages?.forEach((msg) => {
      if (msg !== "") {
        validationMsg += msg + " ";
      }
    });
    menu.push({
      text: "Family Child Care Mentorship Program",
      expandOnClick: true,
      disabled: forceDisable,
      additionalInfo: menteeApp?.description,
      subItems: [
        {
          text: "Mentee Application",
          navPath: `applications/mentee/${awardId}?action=edit&appid=0`,
          disabled: !menteeApp?.can_create,
          disabledText: validationMsg,
        },
      ],
    });
  }

  const getNavPath = (app) => {
    const pwaHomeVisitorAppIds = [528, 529];
    if (pwaHomeVisitorAppIds.includes(app.id)) {
      return applicationsUserInfo.home_visitor_user.can[app.key] &&
        applicationsUserInfo.home_visitor_user.can[app.key].can_initial
        ? `/applications/hv/${app.id}?action=create&appid=0`
        : ``;
      // ? `${baseURL}apps/hv_sc/visitor/${app.key}/initial`
      // : `${baseURL}apps/hvs/create/${app.key}`;
    } else {
      return applicationsUserInfo.home_visitor_user.can[app.key] &&
        applicationsUserInfo.home_visitor_user.can[app.key].can_initial
        ? `${baseURL}apps/hv_sc/visitor/${app.key}/initial`
        : `${baseURL}apps/hvs/create/${app.key}`;
    }
  };

  const homeVisitor = applicationsUserInfo.home_visitor.visitor.info
    // These apps can only be renewed, they cannot be started new, so remove them.
    .filter((item) => !item.title.includes("Early Childhood Home Visitor"))
    .map((app) => ({
      text: app.title,
      navPath: getNavPath(app),
      disabled:
        !(
          (applicationsUserInfo.home_visitor_user.can[app.key] &&
            applicationsUserInfo.home_visitor_user.can[app.key].can_initial) ||
          app.available
        ) || forceDisable,
      disabledText: app["disabled-tooltip"],
    }));
  const homeVisitorSupervisor =
    applicationsUserInfo.home_visitor.supervisor.info
      // These apps can only be renewed, they cannot be started new, so remove them.
      .filter((item) => !item.title.includes("Early Childhood Home Visitor"))
      .map((app) => ({
        text: app.title,
        navPath:
          applicationsUserInfo.home_visitor_user.can[app.key] &&
          applicationsUserInfo.home_visitor_user.can[app.key].can_initial
            ? `${baseURL}apps/hv_sc/visitor/${app.key}/initial`
            : `${baseURL}apps/hvs/create/${app.key}`,
        disabled:
          !(
            (applicationsUserInfo.home_visitor_user.can[app.key] &&
              applicationsUserInfo.home_visitor_user.can[app.key]
                .can_initial) ||
            app.available
          ) || forceDisable,
        disabledText: app["disabled-tooltip"],
      }));
  menu.push({
    text: "Home Visiting Credential",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/ohio-professional-registry/home-visitor/"
          target="_blank"
        >
          {" "}
          Home Visiting - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: [...homeVisitor, ...homeVisitorSupervisor],
    disabled: forceDisable,
  });
  menu.push({
    text: "Infant Mental Health",
    expandOnClick: true,

    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/ohio-professional-registry/ohio-infant-mental-health/"
          target="_blank"
        >
          {" "}
          Ohio Infant Mental Health - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: [
      {
        text: "Ohio Infant Family Affiliate Level I",
        navPath: `${baseURL}apps/imhs/create/487`,
        disabled:
          !applicationsUserInfo.imh_apps["487"].available || forceDisable,
        disabledText: applicationsUserInfo.imh_apps["487"].message,
      },
      {
        text: "Ohio Infant Family Practitioner Level II",
        navPath: `${baseURL}apps/imhs/create/488`,
        disabled:
          !applicationsUserInfo.imh_apps["488"].available || forceDisable,
        disabledText: applicationsUserInfo.imh_apps["488"].message,
      },
      {
        text: "Ohio Infant Mental Health Practitioner Level III",
        navPath: `${baseURL}apps/imhs/create/489`,
        disabled:
          !applicationsUserInfo.imh_apps["489"].available || forceDisable,
        disabledText: applicationsUserInfo.imh_apps["489"].message,
      },
    ],
    disabled: forceDisable,
  });
  menu.push({
    text: "Instructor",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/ohio-professional-registry/instructor/"
          target="_blank"
        >
          {" "}
          Instructor & Professional Development Events - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: [
      {
        text: "Instructor Application",
        navPath: `${baseURL}applications/instructor_application`,
        disabled:
          applicationsUserInfo.has_instructor_app ||
          forceDisable ||
          !(
            applicationsUserInfo.has_verified_post_hs_education &&
            applicationsUserInfo.has_employment
          ),
        disabledText: applicationsUserInfo.has_instructor_app
          ? "You have submitted an instructor application, please edit and view status in the Application Dashboard."
          : applicationsUserInfo.has_verified_post_hs_education &&
            applicationsUserInfo.has_employment
          ? ""
          : "Instructor applicants must have a verified post high school education and employment in their profile.  Once your education has been verified and employment entered, you will be able to apply.",
      },
    ],
    disabled: forceDisable,
  });
  menu.push({
    text: "International Education",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/ohio-professional-registry/international-education-verification/"
          target="_blank"
        >
          {" "}
          International Education Verification - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: [
      {
        text: "International Education Application",
        navPath: `/applications/intedu/468?action=edit&appid=0`,
        disabled: forceDisable,
      },
    ],
    disabled: forceDisable,
  });

  menu.push({
    text: "Ohio PROMISE Professional Early Childhood Inclusion Credential",
    expandOnClick: true,
    subItems:
      applicationsUserInfo.promise_apps &&
      applicationsUserInfo.promise_apps.map((app) => ({
        text: `${app.name} Application`,
        navPath: `applications/promise/${app.id}?action=edit&appid=0`,
        disabled: !app.available || forceDisable,
        additionalInfo: app?.info_message || null,
      })),
    disabled: forceDisable,
  });
  if (applicationsUserInfo.po_stat === "1") {
    menu.push({
      text: "Power OHIO",
      expandOnClick: true,
      additionalInfo: (
        <Box>
          View OCCRRA resources page for additional information on this
          credential.{" "}
          <Link
            href="https://occrra.org/workforce-development/power-ohio-overview/"
            target="_blank"
          >
            {" "}
            POWER Ohio Overview - OCCRRA{" "}
          </Link>
        </Box>
      ),
      subItems: [
        {
          text: "POWER Ohio Application",
          navPath: `${baseURL}apps/powers/create`,
          disabled: !applicationsUserInfo.is_eligible || forceDisable,
          disabledText: applicationsUserInfo.has_power_app
            ? "To be eligible the following conditions must be met: 1. Education (high school - Masters degree ) must be verified in the Education tab of your profile. 2. An ODJFS licensed child care center or family child care home must be entered into the Employment tab of your profile."
            : "",
        },
      ],
      disabled: forceDisable,
    });
  } else {
    menu.push({
      text: "Power OHIO",
      expandOnClick: true,
      additionalInfo: (
        <Box>
          View OCCRRA resources page for additional information on this
          credential.{" "}
          <Link
            href="https://occrra.org/workforce-development/power-ohio-overview/"
            target="_blank"
          >
            {" "}
            POWER Ohio Overview - OCCRRA{" "}
          </Link>
        </Box>
      ),
      subItems: [
        {
          text: "POWER Ohio Application",
          disabled: true,
          disabledText:
            "Currently at capacity due to limited funding. Should additional funding become available programs will be notified.",
        },
      ],
      disabled: forceDisable,
    });
  }
  menu.push({
    text: "School-Age Endorsement",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        <Box sx={{ mb: 2 }}>
          In order to apply for the School-Age Endorsement, you must have a
          verified high school diploma or an Ohio High School Equivalence
          Diploma (formally GED) in your profile. To apply for SAE level II or
          III, you must also have 5 hours of verified Ohio Approved PD.
        </Box>
        <Box>
          View OCCRRA resources page for additional information on this
          credential.{" "}
          <Link
            href="https://occrra.org/ohio-professional-registry/school-age-endorsement/"
            target="_blank"
          >
            {" "}
            School-Age Endorsement - OCCRRA{" "}
          </Link>
        </Box>
      </Box>
    ),
    subItems: applicationsUserInfo.sae_apps.map((app) => ({
      text: app.name,
      navPath: `/applications/sae/${app.id}?action=edit&appid=0`,
      disabled: !app.available || forceDisable,
    })),
    disabled: forceDisable,
  });
  const serviceCoordinatorApps =
    applicationsUserInfo.home_visitor.service_coord.info.filter(
      (item) =>
        item.title.indexOf("Legacy") === -1 &&
        item.title.indexOf("Transition") === -1
    );
  // Reorder items per specififcations
  const lastTwoItems = serviceCoordinatorApps.splice(-2);
  const additionalInfoEisc = (
    <Box>
      <Box sx={{ pb: 2 }}>
        <p>
          See the{" "}
          <Link
            href="https://d2hfgw7vtnz2tl.cloudfront.net/wp-content/uploads/2026/01/EISC-USER-GUIDE-1_26.pdf"
            target="_blank"
          >
            EISC application user guide
          </Link>{" "}
          for more information on the application process.
        </p>
      </Box>
    </Box>
  );
  serviceCoordinatorApps.unshift(...lastTwoItems);
  menu.push({
    text: "Service Coordinator Credential",
    expandOnClick: true,
    additionalInfo: additionalInfoEisc,
    subItems: serviceCoordinatorApps.map((app) => {
      let action = "";
      let disabled = true;
      if (applicationsUserInfo.home_visitor_user.can[app.key].grand_fathered) {
        action = "renew";
        disabled = false;
      }

      if (applicationsUserInfo.home_visitor_user.can[app.key].can_initial) {
        action = "create";
        disabled = false;
      }

      return {
        text: app.title,
        alertTitle: app.title.includes("5 year") ? "Please Review" : null,
        alertText: app.title.includes("5 year")
          ? "You have selected the 5 year Service Coordinator Credential application.  The 5 year credential includes additional requirements above  the 1 year credential. If you meet all the requirements of the 5 year credential, please click Proceed to open the application.  If you're unsure whether or not you meet the 5 year credential requirements, please click Cancel and review the credential criteria prior to submitting an application.  You may return at any time to submit your application."
          : null,
        navPath: `/applications/eisc/${app.id}?action=${action}&appid=0`,
        disabled: disabled || forceDisable,
        infoOnDisabled: true,
        additionalInfo: additionalInfoEisc,
      };
    }),
    disabled: forceDisable,
  });
  menu.push({
    text: "TEACH Early Childhood® OHIO",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/workforce-development/teach-early-childhood-ohio-overview/"
          target="_blank"
        >
          {" "}
          TEACH Early Childhood® OHIO Overview - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: [
      {
        text: "TEACH Early Childhood® OHIO Application",
        navPath: `${baseURL}apps/teachs/create`,
        disabled: !applicationsUserInfo.can_create_teach || forceDisable,
        disabledText: !applicationsUserInfo.can_create_teach
          ? "A verified high school diploma or equivalent, and current employment at an ODJFS Licensed Child Care Center or Family Child Care Home is required to apply."
          : "",
      },
    ],
    disabled: forceDisable,
  });
  menu.push({
    text: "Trauma Informed Care",
    expandOnClick: true,
    additionalInfo: (
      <Box>
        View OCCRRA resources page for additional information on this
        credential.{" "}
        <Link
          href="https://occrra.org/ohio-professional-registry/trauma-informed-care/"
          target="_blank"
        >
          {" "}
          Trauma Informed Care - OCCRRA{" "}
        </Link>
      </Box>
    ),
    subItems: applicationsUserInfo.tic_apps.map((app) => ({
      text: app.name,
      navPath: `/applications/tic/${app.id}?action=edit&appid=0`,
      disabled: !app.available || forceDisable,
    })),
    disabled: forceDisable,
  });

  menu.push({
    text: "Request new Training Organization",
    expandOnClick: true,
    navPath: "/organizationManager/create",
    disabled: forceDisable,
  });

  return menu;
};

const mapInstructorApp = (instructorApp, instructorLists) => {
  const ageGroups = [];
  // Get Core knowledge plain English names
  instructorApp.coreKnowledge =
    instructorApp.levels &&
    instructorApp.coreKnowledge &&
    instructorApp.coreKnowledge.map((id) => {
      const currentLevel = instructorApp.levels.find(
        (lvl) => instructorLists.cbok_translations[id] === lvl.type
      );
      return {
        id,
        name: instructorLists.cbok[id],
        level: currentLevel ? `OA-L${currentLevel.level}` : "",
      };
    });
  // Get age groups plain English names
  for (let key in instructorApp.ageGroups) {
    if (instructorApp.ageGroups[key] === "1") {
      const targetKey = Object.entries(instructorLists.age_translations).find(
        ([atKey, value]) => value === key
      )[0];
      ageGroups.push({
        id: targetKey,
        name: instructorLists.groups[targetKey],
      });
    }
  }
  instructorApp.ageGroups = ageGroups;
  return instructorApp;
};
